import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { normalizeOperationColumns } from "@/views/components/ProTable";
import { Alert, Button, Card, Select, Space, Tag, Typography } from "antd";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { DateRangePicker as ZhDateRangePicker, Table as ZhTable, type TableColumnProps } from "zhihao-ui-react";

import request from "@/api";
import { DEFAULT_VALUE } from "@/config";
import { dateFormat } from "@zhihaoscm/utils";
import Permission from "@/views/components/Permission";
import { useUserStore } from "@/stores/react/authStore";

export interface AiCorpusPublishRow {
    corpusPublish: {
        id: number | string;
        state: number | string;
        updatedTime?: string;
        createdTime?: string;
    };
    corpusList: Array<{ id?: number | string }>;
}

export interface AiCorpusPublishQuery {
    page: number;
    size: number;
    state?: number;
    startTime?: string;
    endTime?: string;
    sortKey?: string;
    sortOrder?: string;
}

export interface AiCorpusPublishPageResult {
    list?: AiCorpusPublishRow[];
    total?: number;
}

export interface AiCorpusPublishPageProps {
    loadPublish?: (query: AiCorpusPublishQuery) => Promise<AiCorpusPublishPageResult>;
    publish?: (id: number) => Promise<unknown>;
    remove?: (id: number) => Promise<unknown>;
    revert?: (id: number) => Promise<unknown>;
}

// 与旧 Vue 接口契约保持默认每页二十条，避免首屏请求和行为用例的分页基线发生变化。
const defaultQuery: AiCorpusPublishQuery = { page: 1, size: 20 };

/** 复用 Vue AI语料发布分页接口。 */
export async function loadAiCorpusPublish(query: AiCorpusPublishQuery): Promise<AiCorpusPublishPageResult> {
    const response = await request.service.get<AiCorpusPublishPageResult>("/corpus-publish/paging", { params: query });
    return response.data;
}

/** 复用 Vue AI语料发布状态操作接口。 */
export async function publishAiCorpus(id: number): Promise<unknown> {
    const response = await request.service.put(`/corpus-publish/publish/${id}`);
    return response.data;
}

export async function deleteAiCorpusPublish(id: number): Promise<unknown> {
    const response = await request.service.delete(`/corpus-publish/${id}`);
    return response.data;
}

export async function revertAiCorpusPublish(id: number): Promise<unknown> {
    const response = await request.service.put(`/corpus-publish/revert/${id}`);
    return response.data;
}

/** 将 Vue 日期筛选转换为后端分页接口的起止时间。 */
export function buildAiCorpusPublishQuery(values: {
    state?: number;
    publishDate?: [string, string];
    sortKey?: string;
    sortOrder?: string;
}): AiCorpusPublishQuery {
    const query: AiCorpusPublishQuery = { ...defaultQuery };
    if (values.state !== undefined) query.state = values.state;
    if (values.publishDate?.[0] && values.publishDate[1]) {
        query.startTime = `${values.publishDate[0]} 00:00:00`;
        query.endTime = `${values.publishDate[1]} 23:59:59`;
    }
    if (values.sortKey) query.sortKey = values.sortKey;
    if (values.sortOrder) query.sortOrder = values.sortOrder;
    return query;
}

function isDraft(row: AiCorpusPublishRow): boolean {
    return Number(row.corpusPublish.state) === 1;
}

function formatDate(value?: string, format = "YYYY-MM-DD HH:mm:ss"): string {
    return value ? dateFormat(value, format) : DEFAULT_VALUE;
}

/** AI语料发布列表，保留 Vue 的筛选字段、状态操作和发布数量边界。 */
export function AiCorpusPublishPage({
    loadPublish = loadAiCorpusPublish,
    publish = publishAiCorpus,
    remove = deleteAiCorpusPublish,
    revert = revertAiCorpusPublish
}: AiCorpusPublishPageProps) {
    const navigate = useNavigate();
    const banButton = useUserStore(state => state.banButton);
    const [rows, setRows] = useState<AiCorpusPublishRow[]>([]);
    const [total, setTotal] = useState(0);
    const [query, setQuery] = useState<AiCorpusPublishQuery>(defaultQuery);
    const [state, setState] = useState<number | undefined>();
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [reloadToken, setReloadToken] = useState(0);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [warningMessage, setWarningMessage] = useState<string | null>(null);

    useEffect(() => {
        let disposed = false;
        void loadPublish(query)
            .then(result => {
                if (disposed) return;
                setRows(result.list || []);
                setTotal(result.total || 0);
                setErrorMessage(null);
            })
            .catch(() => {
                if (disposed) return;
                setRows([]);
                setTotal(0);
                setErrorMessage("AI语料发布加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadPublish, query, reloadToken]);

    const refresh = () => setReloadToken(value => value + 1);
    const runPublish = async (row: AiCorpusPublishRow) => {
        const id = Number(row.corpusPublish.id);
        if (row.corpusList.length < 10) {
            setWarningMessage("至少发布10条语料");
            return;
        }
        try {
            await publish(id);
            setWarningMessage(null);
            refresh();
        } catch {
            setErrorMessage("AI语料发布操作失败");
        }
    };

    const runAction = async (action: (id: number) => Promise<unknown>, row: AiCorpusPublishRow) => {
        try {
            await action(Number(row.corpusPublish.id));
            refresh();
        } catch {
            setErrorMessage("AI语料发布操作失败");
        }
    };

    // 用 zhihao-ui-react 列契约承接旧 Vue 语料发布列表的状态和权限操作插槽。
    const columns: TableColumnProps<AiCorpusPublishRow>[] = [
        {
            key: "publishDate",
            label: "日期",
            minWidth: 106,
            prop: "corpusPublish.updatedTime",
            sortable: true,
            render: ({ row }) => (
                <Button
                    aria-label={`查看发布 ${row.corpusPublish.id}`}
                    type="link"
                    onClick={() => void navigate(`/intelligentAssistant/aiCorpus/publish/info/${row.corpusPublish.id}`)}
                >
                    {formatDate(row.corpusPublish.updatedTime, "YYYY-MM-DD")}
                </Button>
            )
        },
        {
            key: "corpusList",
            label: "语料数量",
            minWidth: 120,
            prop: "corpusList",
            render: ({ row }) => row.corpusList.length
        },
        {
            key: "state",
            label: "状态",
            minWidth: 78,
            prop: "corpusPublish",
            render: ({ row }) => (
                <Tag color={isDraft(row) ? "default" : "success"}>{isDraft(row) ? "草稿" : "已发布"}</Tag>
            )
        },
        {
            key: "createdTime",
            label: "创建时间",
            width: 204,
            prop: "corpusPublish.createdTime",
            sortable: true,
            render: ({ row }) => formatDate(row.corpusPublish.createdTime)
        },
        {
            key: "operation",
            label: "操作",
            fixed: "right",
            width: 240,
            prop: "corpusPublish",
            render: ({ row }) =>
                isDraft(row) ? (
                    <Permission anyOf={["ROLE_ASSISTANTS_CORPUS_W"]}>
                        <Space>
                            <Button
                                aria-label={`发布 ${row.corpusPublish.id}`}
                                disabled={banButton}
                                type="link"
                                onClick={() => void runPublish(row)}
                            >
                                发布
                            </Button>
                            <Button
                                aria-label={`编辑 ${row.corpusPublish.id}`}
                                disabled={banButton}
                                type="link"
                                onClick={() =>
                                    void navigate(`/intelligentAssistant/aiCorpus/publish/edit/${row.corpusPublish.id}`)
                                }
                            >
                                编辑
                            </Button>
                            <Button
                                aria-label={`删除 ${row.corpusPublish.id}`}
                                disabled={banButton}
                                type="link"
                                onClick={() => void runAction(remove, row)}
                            >
                                删除
                            </Button>
                        </Space>
                    </Permission>
                ) : (
                    <Permission anyOf={["ROLE_ASSISTANTS_CORPUS_W"]}>
                        <Button
                            aria-label={`撤回 ${row.corpusPublish.id}`}
                            disabled={banButton}
                            type="link"
                            onClick={() => void runAction(revert, row)}
                        >
                            撤回
                        </Button>
                    </Permission>
                )
        }
    ];

    return (
        <section className="flex h-full min-h-full flex-col" aria-label="ai-corpus-publish-page">
            <Card className="flex h-full flex-1 flex-col [&_.ant-card-body]:flex [&_.ant-card-body]:min-h-0 [&_.ant-card-body]:flex-1 [&_.ant-card-body]:flex-col">
                <ZhTable<AiCorpusPublishRow>
                    data={rows}
                    columns={normalizeOperationColumns(columns)}
                    emptyText="暂无数据"
                    pagination={false}
                    rowKey={row => String(row.corpusPublish.id)}
                    onSortChange={({ prop, order }) => {
                        // 将旧 Vue 发布日期/创建时间排序映射为接口字段。
                        const sortKey =
                            prop === "corpusPublish.createdTime"
                                ? "created_time"
                                : prop === "corpusPublish.updatedTime"
                                  ? "publish_date"
                                  : undefined;
                        if (!sortKey) return;
                        setQuery(current => {
                            const nextQuery = { ...current, page: 1 };
                            if (!order) {
                                delete nextQuery.sortKey;
                                delete nextQuery.sortOrder;
                            } else {
                                nextQuery.sortKey = sortKey;
                                nextQuery.sortOrder = order === "ascending" ? "asc" : "desc";
                            }
                            return nextQuery;
                        });
                    }}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <Typography.Title className="mb-4 mt-0" level={4}>
                                AI语料发布
                            </Typography.Title>

                            {errorMessage ? (
                                <Alert className="mb-4" message={errorMessage} role="alert" type="error" />
                            ) : null}

                            {warningMessage ? (
                                <Alert className="mb-4" message={warningMessage} role="alert" type="warning" />
                            ) : null}

                            <Space className="mb-4" wrap>
                                <Permission anyOf={["ROLE_ASSISTANTS_CORPUS_W"]}>
                                    <Button
                                        aria-label="新增"
                                        disabled={banButton}
                                        type="primary"
                                        onClick={() => void navigate("/intelligentAssistant/aiCorpus/publish/add")}
                                    >
                                        新增
                                    </Button>
                                </Permission>
                                <ZhDateRangePicker
                                    endPlaceholder="发布结束日期"
                                    format="YYYY-MM-DD"
                                    rangeSeparator="-"
                                    startPlaceholder="发布开始日期"
                                    type="daterange"
                                    value={[startDate || null, endDate || null]}
                                    valueFormat="YYYY-MM-DD"
                                    onChange={(_value, formattedValue) => {
                                        setStartDate(formattedValue?.[0] || "");
                                        setEndDate(formattedValue?.[1] || "");
                                    }}
                                />
                                <Select
                                    aria-label="发布状态"
                                    allowClear
                                    placeholder="请选择状态"
                                    value={state}
                                    options={[
                                        { label: "已发布", value: 0 },
                                        { label: "草稿", value: 1 }
                                    ]}
                                    onChange={value => setState(value)}
                                />
                                <Button
                                    aria-label="查询"
                                    type="primary"
                                    onClick={() =>
                                        setQuery(
                                            buildAiCorpusPublishQuery({ state, publishDate: [startDate, endDate] })
                                        )
                                    }
                                >
                                    查询
                                </Button>
                                <Button
                                    aria-label="重置"
                                    onClick={() => {
                                        setState(undefined);
                                        setStartDate("");
                                        setEndDate("");
                                        setQuery(defaultQuery);
                                    }}
                                >
                                    重置
                                </Button>
                            </Space>
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={query.page}
                                pageSize={query.size}
                                total={total}
                                hideOnSinglePage
                                onChange={(page, size) => setQuery(current => ({ ...current, page, size }))}
                            />
                        </div>
                    }
                />
            </Card>
        </section>
    );
}
